/*    
<summary>
   This class component is all about Managing Option data functionality.
   Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import base64 from "base-64";
import { action, computed, makeObservable, observable } from "mobx";
import moment from "moment";
import toast from "react-hot-toast";
import { DATE_FORMAT, safeBase64Encode } from "../../constants/constants";
import userTypeEnum from "../../constants/enums/user-type-enum";
import URLConstants from "../../constants/url-constants";
import config from "../../helpers/config-helper";
import {
  getBusinessTypeIdFromLocalStorage,
  getStoreIdFromLocalStorage,
  getUserIdFromToken,
  getUserTypeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import {
  IUpdateCustomerEmail,
  IUpdateCustomerPassword
} from "../../models/forms/IChangePassword";
import { IObservableInitialState, IOption } from "../../models/ICommon";
import { ICustomerFilter } from "../../models/IFilter";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import {
  ICustomerCSVListVM,
  ICustomerList,
  ICustomerListVM,
  ICustomerVM,
} from "../../models/response/ICustomerManagementResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { formatMessage } from "../../translations/format-message";
import { initialState } from "../initialState/add-edit-cutomer-management-state";
import { initialState as GetAllCustomerState } from "../initialState/get-all-customer-management-state";
import { customerInitialFilterState } from "../initialState/initial-filter-state";
import * as baseService from "../service/base-service";
import { AuthStore } from "./auth-store";
import { advanceSearchInitialState } from "../initialState/advance-search-initial-state";
import genderTypeEnum from "../../constants/enums/gender-type-enum";
import { AuthType } from "../../constants/enums/auth-type-enum";
import { downloadAppointmentsCSV } from "../../constants/appointment-csv-constants";
import { downloadCustomerListCSV } from "../../constants/customer-csv-constants";
import { Col } from "react-bootstrap";

const appConfig = config();


const dateFormat = appConfig.REACT_APP_DATE_FORMAT;
const datetimeFormat = appConfig.REACT_APP_DATETIME_FORMAT;
const authStore = new AuthStore();

export class CustomerManagementStore implements ICommonState {
  inProgress = false;
  error = "";
  customerList: ICustomerListVM = GetAllCustomerState;
  reservationCustomerList: any[] = [];

  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  allCustomerList: any[] = [];
  csvCustomerList: any[] = [];
  addUpdateCustomerState = { ...this.initialStateValue };
  deleteCustomerState = { ...this.initialStateValue };
  activeInactiveState = { ...this.initialStateValue };
  customer: ICustomerVM | undefined = undefined;
  allCustomerState = { ...this.initialStateValue };
  customerState = { ...this.initialStateValue };

  changeCustomerPasswordState = { ...this.initialStateValue };
  changeCustomerEmailState = { ...this.initialStateValue };
  customerReservationListState = { ...this.initialStateValue };
  isMore = true;

  advanceSearchState = { ...this.initialStateValue };
  advanceSearchValues: any = {
    ...advanceSearchInitialState,
    isAdvanceSearchActive: false,
  };

  customerFilter = {
    ...customerInitialFilterState,
  };
  validateCustomerState: IObservableInitialState = {
    ...this.initialStateValue,
  };
  isCustomerValidated: boolean = false;
  customerValidatedData: any = undefined;

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      customerList: observable,
      allCustomerList: observable,
      allCustomerState: observable,
      customer: observable,
      csvCustomerList: observable,
      addUpdateCustomerState: observable,
      deleteCustomerState: observable,
      customerState: observable,
      customerValidatedData: observable,
      reservationCustomerList: observable,
      changeCustomerPasswordState: observable,
      changeCustomerEmailState: observable,
      customerReservationListState: observable,
      customerFilter: observable,
      isMore: observable.deep,
      advanceSearchState: observable,
      advanceSearchValues: observable,
      validateCustomerState: observable,
      isCustomerValidated: observable,
      GetCustomerListService: action,
      GetSearchCustomerRegistrationFilterService: action,
      GetSearchCustomerRegistrationFilterListService: action,
      AdvanceSearchCustomerRegistrationService: action,
      AddCustomerRegister: action,
      AddCustomerService: action,
      UpdateCustomerService: action,
      UpdateCustomerStatusService: action,
      DeleteCustomerService: action,
      GetCustomerByIdService: action,
      GetAllCustomerListService: action,
      GetCustomerListServiceReservation: action,
      ValidateCustomerService: action,
      ValidateEasyCustomerService: action,
      setCustomerDetail: action,
      resetIsCustomerValidated: action,
      reset: action,
      resetOption: action,
      resetCustomerDetail: action,
      resetAddUpdateCustomerState: action,
      UpdateCustomerPasswordService: action,
      UpdateCustomerEmailService: action,
      resetChangePassword: action,
      resetChangeEmail: action,
      resetCustomerReservationList: action,
      setCustomerFilterDetail: action,
      setAdvanceSearchValues: action,
      resetCustomerFilterDetail: action,
      resetAllCustomerList: action,
      resetAdvanceSearchValues: action,
      resetValidateCustomerState: action,
      customerListReservation: computed,
      allCustomer: computed,
      customerDetail: computed,
      customerRegistrationDetail: computed,
      getCustomerFilter: computed,
      getAllCustomer: computed,
    });
  }

  /**
   * This function is used to Get All StaffManagements List data with pagination information by calling an API.
   * @param currentPage : Current Page Number
   * @param pagerSize : Page Size
   * @returns
   */
  GetCustomerListService = (
    currentPage: number,
    pagerSize: number,
    orderBy: string,
    column: string
  ) => {
    this.inProgress = true;
    const businessTypeId = getBusinessTypeIdFromLocalStorage() ?? -1;
    const storeId = getStoreIdFromLocalStorage() ?? -1;
    const urlParams = new URLSearchParams();
    urlParams.append("PageNo", String(currentPage));
    urlParams.append("PageSize", String(pagerSize));
    urlParams.append("OrderBy", orderBy);
    urlParams.append("Column", column);
    if (storeId > 0) {
      urlParams.append("storeId", String(storeId));
    }
    if (businessTypeId > 0) {
      urlParams.append("businessType_id", String(businessTypeId));
    }

    const url = URLConstants.CustomerFilterList + "?" + urlParams.toString();
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ICustomerListVM>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.customerList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GetCustomerListServiceReservation = (
    name: string,
    email: string,
    offset: number,
    pagerSize: number,
    phone: string,
    storeId: number,
    isOtherStores: boolean,
    setHasMore: any,
    setPageSize: any,
    setCustomers: any,
    customerCode: string
  ) => {
    this.customerReservationListState.inProgress = true;
    const url = `/CustomerRegistration/AppointmentFilter?offset=${offset}&email=${email}&PageSize=${pagerSize}&name=${name}&phone=${phone}&isOtherStores=${isOtherStores}&storeId=${storeId}&dianaCode=${customerCode}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.customerReservationListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.reservationCustomerList.push(...response.data?.Data);
          if (
            (name.trim() !== "", email.trim() !== "", phone.trim() !== phone)
          ) {
            setCustomers(response?.data?.Data);
          } else {
            setCustomers((prevItems: any) => [
              ...prevItems,
              ...response?.data?.Data,
            ]);
          }
          // this.reservationCustomerList = response.data.Data;
          if (response?.data?.Data?.length < pagerSize) {
            setHasMore(false);
          }
        }
      })
      .catch((err: string) => {
        this.customerReservationListState.error = err;
      })
      .finally(
        action(() => {
          this.customerReservationListState.inProgress = false;
        })
      );
  };

  get customerListReservation(): any[] {
    if (
      this.reservationCustomerList &&
      this.reservationCustomerList?.length > 0
    ) {
      return this.reservationCustomerList?.map((customer) => {
        return {
          id: customer.Id,
          lastName: customer.LastName,
          firstName: customer.FirstName,
          lastNameKana: customer.LastNameKana,
          firstNameKana: customer.FirstNameKana,
          email: customer.Email,
        };
      });
    }
    return [];
  }

  resetCustomerReservationList = () => {
    this.reservationCustomerList = [];
    this.customerReservationListState = { ...this.initialStateValue };
  };

  GetAllCustomerListService = () => {
    this.allCustomerState.inProgress = true;
    const storeId = getStoreIdFromLocalStorage() ?? -1;
    const url = URLConstants.CustomerList + "?StoreId=" + storeId;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.allCustomerState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.allCustomerList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.allCustomerState.error = err;
      })
      .finally(
        action(() => {
          this.allCustomerState.inProgress = false;
        })
      );
  };

  get getAllCustomer(): IOption[] {
    const options: IOption[] = [
      {
        id: -1,
        value: formatMessage("please_select"),
        label: formatMessage("please_select"),
        disabled: false,
      },
    ];

    if (this.allCustomerList && this.allCustomerList?.length > 0) {
      this.allCustomerList?.map((option: any) => {
        let tempFirstName = (option.FirstName ? option.FirstName : "")
        let tempEnglishName = (option.LastName ? (option.LastName  + " " + tempFirstName) : "" + tempFirstName)
        options.push({
          id: option.Id,  
          value: (option.LastName || option.FirstName) ? tempEnglishName : (option.LastNameKana + " " + option.FirstNameKana),
          label: (option.LastName || option.FirstName) ? tempEnglishName : (option.LastNameKana + " " + option.FirstNameKana),
          disabled: false,
          data: { ...option },
        });
      });
    }
    return options;
  }

  resetAllCustomerList = () => {
    this.allCustomerList = [];
    this.customer = undefined;
    this.customerState = { ...this.initialStateValue };
    this.addUpdateCustomerState = { ...this.initialStateValue };
  };

  /**
   * This function is used to fetch the list of customer registrations based on the provided search query and pagination details.
   * @param currentPage : Current Page Number (used for pagination)
   * @param pagerSize : Number of items per page (used for pagination)
   * @param searchQuery : Search query string that filters customer registrations
   * @returns
   */
  GetSearchCustomerRegistrationFilterService = (
    currentPage: number,
    pagerSize: number,
    searchQuery: string = customerInitialFilterState.SearchQuery ?? "",
    isBlocked = customerInitialFilterState.IsBlocked ?? null,
    firstNameKana: string = customerInitialFilterState.FirstNameKana ?? "",
    phone1: string = customerInitialFilterState.PhoneNumber ?? "",
    orderBy: string,
    column: string,
    customerCode: string = customerInitialFilterState.CustomerCode ?? ""
  ) => {
    this.inProgress = true;
    const businessTypeId = getBusinessTypeIdFromLocalStorage() ?? -1;
    const storeId = getStoreIdFromLocalStorage() ?? -1;
    const urlParams = new URLSearchParams();
    urlParams.append("PageNo", String(currentPage));
    urlParams.append("PageSize", String(pagerSize));
    urlParams.append("OrderBy", orderBy);
    urlParams.append("Column", column);
    if (storeId > 0) {
      urlParams.append("StoreId", String(storeId));
    }
    if (businessTypeId > 0) {
      urlParams.append("businessType_id", String(businessTypeId));
    }

    let url = URLConstants.CustomerFilterList + "?" + urlParams.toString();
    if (isBlocked !== null) {
      url += `&is_blocked=${isBlocked}`;
    } else {
      url += `&is_blocked=`;
    }
    if (searchQuery) url += `&Search=${searchQuery}`;
    if (firstNameKana) url += `&firstNameKana=${firstNameKana}`;
    if (phone1) url += `&phone1=${phone1}`;
    if (getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store])
      url = url + "&StoreId=" + getStoreIdFromLocalStorage();
    if (customerCode) url += `&dianaCode=${customerCode}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ICustomerListVM>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.customerList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GetSearchCustomerRegistrationFilterListService = (
    searchQuery: string = customerInitialFilterState.SearchQuery ?? "",
    isBlocked = customerInitialFilterState.IsBlocked ?? null,
    firstNameKana: string = customerInitialFilterState.FirstNameKana ?? "",
    phone1: string = customerInitialFilterState.PhoneNumber ?? "",
    orderBy: string,
    column: string,
    customerCode: string = customerInitialFilterState.CustomerCode ?? "",
    data: any,
    isAdvanceSearchActive: boolean
  ) => {
    this.inProgress = true;
    let userType = getUserTypeFromLocalStorage();
    let tempValues = { ...data };
    let gender = null;
    if (tempValues.IsMale && tempValues.IsFemale && tempValues.IsNotSet)
      gender = null;
    else if (tempValues.IsMale && tempValues.IsFemale)
      gender = genderTypeEnum.MaleFemale;
    else if (tempValues.IsFemale && tempValues.IsNotSet)
      gender = genderTypeEnum.FemaleNotSet;
    else if (tempValues.IsMale && tempValues.IsNotSet)
      gender = genderTypeEnum.MaleNotSet;
    else if (tempValues.IsMale) gender = genderTypeEnum.Male;
    else if (tempValues.IsFemale) gender = genderTypeEnum.Female;
    else if (tempValues.IsNotSet) gender = genderTypeEnum.NotSet;
    else gender = null;
    let noOfVisit = tempValues.NumberOfVisit !== "" ? parseInt(tempValues.NumberOfVisit) : tempValues.NumberOfVisit;
    let noOfVisitTo = tempValues.NumberOfVisitTo !== "" ? parseInt(tempValues.NumberOfVisitTo) : tempValues.NumberOfVisitTo;
    let requestBody = {
      IsAdvanceSearchActive: isAdvanceSearchActive,
      FirstNameKana: firstNameKana || null,
      Search : searchQuery || null,
      DianaCode : customerCode || null,
      IsBlocked : isBlocked !== null ? isBlocked : false,
      OrderBy: orderBy,
      Column: column,
      IsVisit:
        tempValues.IsVisitCanBe && !tempValues.IsVisitCantBe
          ? true
          : !tempValues.IsVisitCanBe && tempValues.IsVisitCantBe
          ? false
          : null,
      IsLastVisit:
        tempValues.IsLastVisitCanBe && !tempValues.IsLastVisitCantBe
          ? true
          : !tempValues.IsLastVisitCanBe && tempValues.IsLastVisitCantBe
          ? false
          : null,
      AgeGroup:
        tempValues.AgeGroup > 0 && tempValues.AgeGroup !== ""
          ? (typeof tempValues.AgeGroup === "string"
            ? parseInt(tempValues.AgeGroup)
            : tempValues.AgeGroup)
          : (tempValues.AgeGroup === "-2" ? -1 : null),
      Agreement:
        tempValues.AgreementId > 0 && tempValues.AgreementId !== ""
          ? tempValues.AgreementId
          : null,
      Gender: gender,
      IsReservation: tempValues.IsReservation
        ? tempValues.IsReservation === "0"
          ? true
          : false
        : null,
      IsNomiantionFee:
        (tempValues.IsNominationFeeCanBe && tempValues.IsNominationFeeCantBe) ||
        (!tempValues.IsNominationFeeCanBe && !tempValues.IsNominationFeeCantBe)
          ? null
          : tempValues.IsNominationFeeCanBe
          ? true
          : tempValues.IsNominationFeeCantBe
          ? false
          : null,
      IsRefer:
        (tempValues.IsRefererCanBe && tempValues.IsRefererCantBe) ||
        (!tempValues.IsRefererCanBe && !tempValues.IsRefererCantBe)
          ? null
          : tempValues.IsRefererCanBe
          ? true
          : tempValues.IsRefererCantBe
          ? false
          : null,
      IsDm:
        (tempValues.IsDMYes && tempValues.IsDMNo) ||
        (!tempValues.IsDMYes && !tempValues.IsDMNo)
          ? null
          : tempValues.IsDMYes
          ? true
          : tempValues.IsDMNo
          ? false
          : null,
      NumberOfVisit: noOfVisit ? noOfVisit : null,
      NumberOfVisitTo: noOfVisitTo ? noOfVisitTo : null,
      ReasonOfVisiting:
        tempValues.ReasonOfVisiting > 0 ? tempValues.ReasonOfVisiting : null,
      FirstName: data.FirstName || null,
      LastName: data.LastName || null,
      Phone1: isAdvanceSearchActive == true ? (data.Phone1 || null) : (phone1 || null),
      DOB: data.Dob || null,
      EndDOB: data.EndDOB || null,
      VisitStartDate:
        data.IsVisitCantBe || data.IsVisitCanBe
          ? data.VisitStartDate || null
          : null,
      VisitEndDate:
        data.IsVisitCantBe || data.IsVisitCanBe
          ? data.VisitEndDate || null
          : null,
      LastVisitStartDate:
        data.IsLastVisitCantBe || data.IsLastVisitCanBe
          ? data.LastVisitStartDate || null
          : null,
      LastVisitEndDate:
        data.IsLastVisitCantBe || data.IsLastVisitCanBe
          ? data.LastVisitEndDate || null
          : null,
      StoreId:
        userType === userTypeEnum[userTypeEnum.Store]
          ? getStoreIdFromLocalStorage()
          : null,
      BusinessType: getBusinessTypeIdFromLocalStorage(),
      PagingDetails: {
        PageNo: 1,
        PageSize: 5000,
      },
    };


    let url = URLConstants.CustomerFilterList + "/CSV";
    return baseService
      .postRequest(url, requestBody)
      .then(
        (response: IApiResponse<IApiSuccessResponse<ICustomerCSVListVM>>) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.csvCustomerList = response.data.Data.CustomerRegistrationList;
            if (this.csvCustomerList?.length > 0) {
              downloadCustomerListCSV(
                getUserTypeFromLocalStorage() == userTypeEnum[5] ? true : false,
                
                this.csvCustomerList,
                `顧客一覧-${moment().format("YYYYMMDD_HHmmss")}`
              );
            }
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  AdvanceSearchCustomerRegistrationService = (
    currentPage: number,
    pagerSize: number,
    data: any
  ) => {
    this.advanceSearchState.inProgress = true;
    let url = URLConstants.AdvanceSearch;
    let userType = getUserTypeFromLocalStorage();
    let tempValues = { ...data };
    let gender = null;
    if (tempValues.IsMale && tempValues.IsFemale && tempValues.IsNotSet)
      gender = null;
    else if (tempValues.IsMale && tempValues.IsFemale)
      gender = genderTypeEnum.MaleFemale;
    else if (tempValues.IsFemale && tempValues.IsNotSet)
      gender = genderTypeEnum.FemaleNotSet;
    else if (tempValues.IsMale && tempValues.IsNotSet)
      gender = genderTypeEnum.MaleNotSet;
    else if (tempValues.IsMale) gender = genderTypeEnum.Male;
    else if (tempValues.IsFemale) gender = genderTypeEnum.Female;
    else if (tempValues.IsNotSet) gender = genderTypeEnum.NotSet;
    else gender = null;
    let noOfVisit = tempValues.NumberOfVisit !== "" ? parseInt(tempValues.NumberOfVisit) : tempValues.NumberOfVisit;
    let noOfVisitTo = tempValues.NumberOfVisitTo !== "" ? parseInt(tempValues.NumberOfVisitTo) : tempValues.NumberOfVisitTo;
    let requestBody = {
      IsVisit:
        tempValues.IsVisitCanBe && !tempValues.IsVisitCantBe
          ? true
          : !tempValues.IsVisitCanBe && tempValues.IsVisitCantBe
          ? false
          : null,
      IsLastVisit:
        tempValues.IsLastVisitCanBe && !tempValues.IsLastVisitCantBe
          ? true
          : !tempValues.IsLastVisitCanBe && tempValues.IsLastVisitCantBe
          ? false
          : null,
      AgeGroup:
        tempValues.AgeGroup > 0 && tempValues.AgeGroup !== ""
          ? (typeof tempValues.AgeGroup === "string"
            ? parseInt(tempValues.AgeGroup)
            : tempValues.AgeGroup)
          : (tempValues.AgeGroup === "-2" ? -1 : null),
      Agreement:
        tempValues.AgreementId > 0 && tempValues.AgreementId !== ""
          ? tempValues.AgreementId
          : null,
      Gender: gender,
      IsReservation: tempValues.IsReservation
        ? tempValues.IsReservation === "0"
          ? true
          : false
        : null,
      IsNomiantionFee:
        (tempValues.IsNominationFeeCanBe && tempValues.IsNominationFeeCantBe) ||
        (!tempValues.IsNominationFeeCanBe && !tempValues.IsNominationFeeCantBe)
          ? null
          : tempValues.IsNominationFeeCanBe
          ? true
          : tempValues.IsNominationFeeCantBe
          ? false
          : null,
      IsRefer:
        (tempValues.IsRefererCanBe && tempValues.IsRefererCantBe) ||
        (!tempValues.IsRefererCanBe && !tempValues.IsRefererCantBe)
          ? null
          : tempValues.IsRefererCanBe
          ? true
          : tempValues.IsRefererCantBe
          ? false
          : null,
      IsDm:
        (tempValues.IsDMYes && tempValues.IsDMNo) ||
        (!tempValues.IsDMYes && !tempValues.IsDMNo)
          ? null
          : tempValues.IsDMYes
          ? true
          : tempValues.IsDMNo
          ? false
          : null,
      NumberOfVisit: noOfVisit ? noOfVisit : null,
      NumberOfVisitTo: noOfVisitTo ? noOfVisitTo : null,
      ReasonOfVisiting:
        tempValues.ReasonOfVisiting > 0 ? tempValues.ReasonOfVisiting : null,
      FirstName: data.FirstName || null,
      LastName: data.LastName || null,
      Phone1: data.Phone1 || null,
      DOB: data.Dob || null,
      EndDOB: data.EndDOB || null,
      VisitStartDate:
        data.IsVisitCantBe || data.IsVisitCanBe
          ? data.VisitStartDate || null
          : null,
      VisitEndDate:
        data.IsVisitCantBe || data.IsVisitCanBe
          ? data.VisitEndDate || null
          : null,
      LastVisitStartDate:
        data.IsLastVisitCantBe || data.IsLastVisitCanBe
          ? data.LastVisitStartDate || null
          : null,
      LastVisitEndDate:
        data.IsLastVisitCantBe || data.IsLastVisitCanBe
          ? data.LastVisitEndDate || null
          : null,
      StoreId:
        userType === userTypeEnum[userTypeEnum.Store]
          ? getStoreIdFromLocalStorage()
          : null,
      BusinessType: getBusinessTypeIdFromLocalStorage(),
      PagingDetails: {
        PageNo: currentPage,
        PageSize: pagerSize,
      },
    };
    return baseService
      .postRequest(url, requestBody)
      .then((response: IApiResponse<IApiSuccessResponse<ICustomerListVM>>) => {
        if (response.data.Error) {
          this.advanceSearchState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.customerList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.advanceSearchState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.advanceSearchState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to map customerList to allCustomer List suitable for Grid Component.
   * @returns allCustomer
   */
  get allCustomer(): ICustomerList[] {
    if (
      this.customerList?.CustomerList &&
      this.customerList.CustomerList.length > 0
    ) {
      return this.customerList.CustomerList.map((customer) => {
        return {
          Id: customer.Id,
          LastName: customer.LastName,
          FirstName: customer.FirstName,
          LastNameKana: customer.LastNameKana,
          FirstNameKana: customer.FirstNameKana,
          Dob: customer.Dob,
          Gender: customer.Gender,
          Phone1: customer.Phone1,
          // Phone2: customer.Phone2,
          Email: customer.Email,
          PostalCode: customer.PostalCode,
          Address: customer.Address,
          // PrefectureName: customer.PostalCode ? customer.PrefectureName : "",
          PrefectureName: customer.PrefectureName ?? "",
          LastVisitTime: customer.LastVisitTime
            ? moment(customer.LastVisitTime)?.format("YYYY-MM-DD HH:mm:ss")
            : "-",
          DianaCode: customer.DianaCode ?? "",
          AppMembershipCode: customer.AppMemberShipCode ?? "",
          Referer: customer.Referer ?? "",
          CustomerMotiveDetails: customer.CustomerMotiveList ?? [],
          Memo: customer.Memo ?? "",
          DmDshs: customer.DmDshs,
          Status: customer.Status,
          IsBlocked: customer.IsBlocked,
          TotalCount: customer.TotalCount,
          isDelete: true,
          isEdit: true,
        };
      });
    }
    return [];
  }

  /**
   * This function is used to Get Customer Details by Id by calling an API.
   * @param id : The Customer Identifier
   * @returns
   */
  GetCustomerByIdService = (id: number, storeId: number | null = null) => {
    this.customerState.inProgress = true;
    let url = `${URLConstants.GetCustomerById}/${id}`;
    if (storeId !== null)
      url = `${URLConstants.GetCustomerById}/${id}?StoreId=${storeId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ICustomerVM>>) => {
        if (response.data.Error) {
          this.customerState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.customer = data;
        }
      })
      .catch((err: string) => {
        this.customerState.error = err;
      })
      .finally(
        action(() => {
          this.customerState.inProgress = false;
        })
      );
  };

  /**
   * This function provides initail values to the Add Customer Form.
   * @returns Customer Details
   *
   */
  get customerDetail(): any {
    if (this.customer) {
      return {
        Id: this.customer.Id ?? 0,
        FirstName: this.customer.FirstName ?? "",
        LastName: this.customer.LastName ?? "",
        FirstNameKana: this.customer.FirstNameKana ?? "",
        LastNameKana: this.customer.LastNameKana ?? "",
        Dob: this.customer.Dob
          ? moment(this.customer.Dob).format(dateFormat)
          : "",
        Gender: this.customer.Gender?.toString() || "2",
        Phone1: this.customer.Phone1 ?? "",
        // Phone2: this.customer.Phone2 ?? "",
        Email: this.customer.Email ?? "",
        PostalCode: this.customer.PostalCode
          ? String(this.customer.PostalCode)
          : "",
        Address: this.customer.Address ?? "",
        PrefectureName: this.customer.PrefectureName ?? "",
        DianaCode: this.customer.DianaCode ?? "",
        AppMemberShipCode: this.customer.AppMemberShipCode ?? "",
        Referer: this.customer.Referer ?? -1,
        Memo: this.customer.Memo ?? "",
        DmDshs: this.customer.DmDshs.toString(),
        Status: this.customer.Status?.toString() || "0",
        IsBlocked: this.customer.IsBlocked ?? false,
        DobYear: this.customer.Dob
          ? Number(moment(this.customer.Dob).format(dateFormat).split("-")[0])
          : -1,
        DobMonth: this.customer.Dob
          ? Number(moment(this.customer.Dob).format(dateFormat).split("-")[1])
          : -1,
        DobDate: this.customer.Dob
          ? Number(moment(this.customer.Dob).format(dateFormat).split("-")[2])
          : -1,

        CustomerMotiveDetails: Array.isArray(this.customer.CustomerMotiveList)
          ? this.customer.CustomerMotiveList.map((motive) => ({
              MId: motive.MId,
              CustId: motive.CustId,
            }))
          : [],

        MotivationOptions: Array.isArray(this.customer.CustomerMotiveList)
          ? this.customer.CustomerMotiveList.map((motive) => String(motive.MId))
          : [],
        TotalCount: this.customer.TotalCount || 0,
        CustomerType:
          typeof this.customer.CustomerType === "string"
            ? parseInt(this.customer.CustomerType)
            : this.customer.CustomerType,
        FirstVisitDate: this.customer.FirstVisitDate
          ? moment(this.customer.FirstVisitDate).format(DATE_FORMAT)
          : "",
        LastVisitDate: this.customer.LastVisitDate
          ? moment(this.customer.LastVisitDate).format(DATE_FORMAT)
          : "",
        Password: "",
        ConfirmPassword: "",
      };
    }
    return initialState;
  }
  
  get customerRegistrationDetail(): any {
    if (this.customer) {
      return {
        Id: this.customer.Id ?? 0,
        FirstName: this.customer.FirstName ?? "",
        LastName: this.customer.LastName ?? "",
        Dob: this.customer.Dob
          ? moment(this.customer.Dob).format(dateFormat)
          : "",
        Gender: this.customer.Gender?.toString() || "2",
        // Phone2: this.customer.Phone2 ?? "",
        PostalCode: this.customer.PostalCode
          ? String(this.customer.PostalCode)
          : "",
        Address: this.customer.Address ?? "",
        PrefectureName: this.customer.PrefectureName ?? "",
        DianaCode: this.customer.DianaCode ?? "",
        AppMemberShipCode: this.customer.AppMemberShipCode ?? "",
        Referer: this.customer.Referer ?? -1,
        Memo: this.customer.Memo ?? "",
        DmDshs: this.customer.DmDshs?.toString(),
        Status: this.customer.Status?.toString() || "0",
        IsBlocked: this.customer.IsBlocked ?? false,
        DobYear: this.customer.Dob
          ? Number(moment(this.customer.Dob).format(dateFormat).split("-")[0])
          : -1,
        DobMonth: this.customer.Dob
          ? Number(moment(this.customer.Dob).format(dateFormat).split("-")[1])
          : -1,
        DobDate: this.customer.Dob
          ? Number(moment(this.customer.Dob).format(dateFormat).split("-")[2])
          : -1,

        CustomerMotiveDetails: Array.isArray(this.customer.CustomerMotiveList)
          ? this.customer.CustomerMotiveList.map((motive) => ({
              MId: motive.MId,
              CustId: motive.CustId,
            }))
          : [],

        MotivationOptions: Array.isArray(this.customer.CustomerMotiveList)
          ? this.customer.CustomerMotiveList.map((motive) => String(motive.MId))
          : [],
        TotalCount: this.customer.TotalCount || 0,
        CustomerType:
          typeof this.customer.CustomerType === "string"
            ? parseInt(this.customer.CustomerType)
            : this.customer.CustomerType,
        FirstVisitDate: this.customer.FirstVisitDate
          ? moment(this.customer.FirstVisitDate).format(DATE_FORMAT)
          : "",
        LastVisitDate: this.customer.LastVisitDate
          ? moment(this.customer.LastVisitDate).format(DATE_FORMAT)
          : "",
      };
    }
    return initialState;
  }

  setCustomerDetail = (data:any) => {
    this.customer = data;
  }

  /*
    This function is used to reset all customer observables to their initial values.  
    */
  resetCustomerDetail = () => {
    this.customer = undefined;
    this.customerState = { ...this.initialStateValue };
  };

  /**
   * This function is used to Add New Customer by calling an API.
   * @param data : Customer Data
   * @returns
   */

  AddCustomerRegister = (data: any, RegisterCustomerlogin: (data:{Email: string; Password: string}) => void) => {
    this.addUpdateCustomerState.inProgress = true;
    let url = URLConstants.AddCustomerRegister;
    const requestData = {
      FirstName: data.FirstName || null,
      LastName: data.LastName || null,
      FirstNameKana: data.FirstNameKana || null,
      LastNameKana: data.LastNameKana || null,
      Dob: data.Dob || null,
      Gender: data.Gender ?? null,
      Phone1: data.Phone1 && data.Phone1.trim() !== "" ? data.Phone1 : null,
      // Phone2: data.Phone2 && data.Phone2.trim() !== "" ? data.Phone2 : null,
      Email: data.Email.toLowerCase() || null,
      PostalCode: data.PostalCode ? String(data.PostalCode) : null,
      Address: data.Address || "",
      PrefectureName: data.PrefectureName || null,
      DianaCode: data.DianaCode || null,
      AppMemberShipCode: data.AppMemberShipCode || null,
      Referer: data.Referer || null,
      IsBlocked: data.IsBlocked ?? null,
      Memo: data.Memo || null,
      DmDshs:
        data.DmDshs === true || data.DmDshs === false ? data.DmDshs : false,
      Password: safeBase64Encode(data.Password) || null,
      CustomerType: data.CustomerType ?? null,
      RegisteredBy: data.RegisteredBy ?? null,
      StoreId: data.StoreId ?? null,
      UserId: data.UserId ?? null,
      Status: data.Status,
      CustomerMotiveDetails:
        data.CustomerMotiveDetails?.map((detail: any) => ({
          MId: detail.MId ?? null,
          CustId: detail.CustId ?? null,
        })) || [],
      IsOverwriteEmail: data.IsOverwriteEmail,
    };

    return baseService
      .postRequest(url, requestData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addUpdateCustomerState.error = response.data.Message;
        } else {
          if (response.data.Data > 0) {
            RegisterCustomerlogin({
              Email: data.Email,
              Password: safeBase64Encode(data.Password)
            });
          }
          this.addUpdateCustomerState.success = true;

        }
      })
      .catch((err: string) => {
        this.addUpdateCustomerState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateCustomerState.inProgress = false;
        })
      );
  };

  AddCustomerService = (data: any) => {
    this.addUpdateCustomerState.inProgress = true;
    const businessTypeId = getBusinessTypeIdFromLocalStorage() ?? -1;
    const storeId = getStoreIdFromLocalStorage() ?? -1;
    const urlParams = new URLSearchParams();

    const requestData = {
      ...data,
      RegistrationType: data.RegistrationType?.toString(),
      PostalCode: data.PostalCode == "" ? null : String(data.PostalCode),
    };

    if (storeId > 0) {
      urlParams.append("storeId", String(storeId));
      // requestData.StoreId = storeId;
    }
    if (businessTypeId > 0) {
      urlParams.append("BusinessType", String(businessTypeId));
      // requestData.BusinessTypeId = businessTypeId;
    }

    let url = URLConstants.AddCustomer + "?" + urlParams.toString();

    let userType = getUserTypeFromLocalStorage();
    requestData.StoreId =
      userType === userTypeEnum[userTypeEnum.Store]
        ? getStoreIdFromLocalStorage()
        : null;
    requestData.BusinessType =
      userType === userTypeEnum[userTypeEnum.Store] ||
      userType === userTypeEnum[userTypeEnum.HeadQuarter]
        ? getBusinessTypeIdFromLocalStorage()
        : null;
    requestData.UserId = getUserIdFromToken();
    return baseService
      .postRequest(url, requestData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateCustomerState.error = response.data.Message;
        } else {
          this.addUpdateCustomerState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateCustomerState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateCustomerState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Updating Existing Customer Details by calling an API.
   * @param id : The Customer Identifier
   * @param data
   * @returns
   */
  UpdateCustomerService = (id: number, data: any, isProfile: boolean = false) => {
    this.addUpdateCustomerState.inProgress = true;
    let url = `${URLConstants.UpdateCustomer}`;

    const requestData = {
      Id: id,
      ...data,
      RegistrationType: data.RegistrationType?.toString(),
      PostalCode: data.PostalCode == "" ? null : String(data.PostalCode),
      StoreId:
        getStoreIdFromLocalStorage() != -1
          ? getStoreIdFromLocalStorage()
          : null,
    };
    if(isProfile)
      requestData.StoreId = null;

    return baseService
      .putRequest(url, requestData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateCustomerState.error = response.data.Message;
        } else {
          this.addUpdateCustomerState.success = true;
          let userTypeFromLocalStorage = getUserTypeFromLocalStorage();
          return this.GetCustomerByIdService(
            id,
            isProfile ? null :
            (userTypeFromLocalStorage !==
              userTypeEnum[userTypeEnum.SuperAdmin] &&
              userTypeFromLocalStorage !==
                userTypeEnum[userTypeEnum.HeadQuarter]
              ? getStoreIdFromLocalStorage()
              : null)
          );
        }
      })
      .catch((err: string) => {
        this.addUpdateCustomerState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateCustomerState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddCustomer observables to their initial values.  
    */
  resetAddUpdateCustomerState = () => {
    this.addUpdateCustomerState.inProgress = false;
    this.addUpdateCustomerState.success = false;
    this.addUpdateCustomerState.error = "";
  };

  /**
   * This function is used to Delete Customer by calling an API.
   * @param id : The Customer Identifier
   * @returns
   */
  DeleteCustomerService = (id: number) => {
    this.deleteCustomerState.inProgress = true;
    let url = `${URLConstants.DeleteCustomer}/${id}`;

    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteCustomerState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.deleteCustomerState.success = true;
        }
      })
      .catch((err: string) => {
        this.deleteCustomerState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.deleteCustomerState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetDeleteCustomer = () => {
    this.deleteCustomerState.inProgress = false;
    this.deleteCustomerState.success = false;
    this.deleteCustomerState.error = "";
  };

  /**
   * This function is used to Updating Existing StaffManagement state (i.e. active/inactive) Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param state: StaffManagement state
   * @returns
   */
  UpdateCustomerStatusService = (id: number, state: boolean) => {
    this.activeInactiveState.inProgress = true;
    let url = URLConstants.CustomerStatus;
    let customerData: any = {
      Id: id,
      IsActive: state,
    };
    return baseService
      .putRequest(url, customerData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activeInactiveState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.activeInactiveState.success = true;
      })
      .catch((err: string) => {
        this.activeInactiveState.error = err;
      })
      .finally(
        action(() => {
          this.activeInactiveState.inProgress = false;
        })
      );
  };

  UpdateCustomerPasswordService = (data: IUpdateCustomerPassword) => {
    this.changeCustomerPasswordState.inProgress = true;
    let url = URLConstants.ChangeCustomerPassword;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.changeCustomerPasswordState.error = response.data.Message;
        } else this.changeCustomerPasswordState.success = true;
      })
      .catch((err: any) => {
        this.changeCustomerPasswordState.error = err;
      })
      .finally(
        action(() => {
          this.changeCustomerPasswordState.inProgress = false;
        })
      );
  };

  resetChangePassword = () => {
    this.changeCustomerPasswordState = { ...this.initialStateValue };
  };

  UpdateCustomerEmailService = (data: IUpdateCustomerEmail) => {
    this.changeCustomerEmailState.inProgress = true;
    let url = URLConstants.ChangeCustomerEmail;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.changeCustomerEmailState.error = response.data.Message;
        } else {
          this.changeCustomerEmailState.success = true;
          return this.GetCustomerByIdService(
            data.CustomerId
          );
        }
      })
      .catch((err: any) => {
        this.changeCustomerEmailState.error = err;
      })
      .finally(
        action(() => {
          this.changeCustomerEmailState.inProgress = false;
        })
      );
  };

  resetChangeEmail = () => {
    this.changeCustomerEmailState = { ...this.initialStateValue };
  };

  ValidateCustomerService = (data: any) => {
    this.validateCustomerState.inProgress = true;
    let url = `${URLConstants.ValidateCustomerRegistration}`;

    return baseService
      .postRequest(url, { ...data })
      .then((response: IApiResponse<IApiSuccessResponse<number>>) => {
        if (response.data.Error) {
          if(response.data.Message === "IsThereADifferentEmailIdForThisAccount"){
            this.GetCustomerByIdService(response.data.Data);
          }
          this.validateCustomerState.error = response.data.Message;
        } else {
          this.validateCustomerState.success = true;
          this.customerValidatedData = {
            Email: data.Email,
            KanaName: data.LastNameKana + " " + data.FirstNameKana,
            AuthType: AuthType.CustomerRegistrationAuth,
          };
          this.isCustomerValidated = true;
        }
      })
      .catch((err: string) => {
        this.validateCustomerState.error = err;
      })
      .finally(
        action(() => {
          this.validateCustomerState.inProgress = false;
        })
      );
  };

    ValidateEasyCustomerService = (data: any) => {
    this.validateCustomerState.inProgress = true;
    let url = `${URLConstants.ValidateCustomerRegistration}`;

    return baseService
      .postRequest(url, { ...data })
      .then((response: IApiResponse<IApiSuccessResponse<number>>) => {
        if (response.data.Error) {
          if(response.data.Message === "IsThereADifferentEmailIdForThisAccount"){
            this.GetCustomerByIdService(response.data.Data);
          }
          this.validateCustomerState.error = response.data.Message;
        } else {
          this.validateCustomerState.success = true;
          this.customerValidatedData = {
            Email: data.Email,
            KanaName: data.LastNameKana + " " + data.FirstNameKana,
            AuthType: AuthType.CustomerRegistrationAuth,
          };
          // this.isCustomerValidated = true;
        }
      })
      .catch((err: string) => {
        this.validateCustomerState.error = err;
      })
      .finally(
        action(() => {
          this.validateCustomerState.inProgress = false;
        })
      );
  };

  resetValidateCustomerState = () => {
    this.validateCustomerState = { ...this.initialStateValue };
  };

  setCustomerFilterDetail = (filter: ICustomerFilter) => {
    this.customerFilter = { ...filter };
  };

  setAdvanceSearchValues = (searchFields: any) => {
    this.advanceSearchValues = { ...searchFields };
  };

  resetAdvanceSearchValues = () => {
    this.advanceSearchValues = {
      ...advanceSearchInitialState,
      isAdvanceSearchActive: false,
    };
  };

  resetCustomerFilterDetail = () => {
    this.customerFilter = { ...customerInitialFilterState };
  };

  resetIsCustomerValidated = () => {
    this.isCustomerValidated = false;
    this.customerValidatedData = undefined;
  };

  get getCustomerFilter(): ICustomerFilter {
    return this.customerFilter;
  }

  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateCustomerState.success = false;
    this.addUpdateCustomerState.error = "";
    this.deleteCustomerState = { ...this.initialStateValue };
    this.addUpdateCustomerState.inProgress = false;
    this.customerState = { ...this.initialStateValue };
    this.isCustomerValidated = false;
    this.validateCustomerState = { ...this.initialStateValue };
    this.advanceSearchState = { ...this.initialStateValue };
    this.advanceSearchValues = { ...advanceSearchInitialState };
  };

  /**
   * This function is used to reset all customer observables to their initial values.
   * @returns
   */
  resetOption = () => {
    this.error = "";
    this.inProgress = false;
    this.customerList = GetAllCustomerState;
    this.addUpdateCustomerState = { ...this.initialStateValue };
    this.deleteCustomerState = { ...this.initialStateValue };
    this.customer = undefined;
    this.customerState = { ...this.initialStateValue };
  };
}

export default new CustomerManagementStore();
